import { useEffect, useState } from 'react';
import { usePdfStore } from '@/stores/pdfStore';
import {
  setProtectionPassword,
  validateProtectionPasswords,
  type PdfEncryptionAlgorithm,
} from '@/lib/pdf/pdfProtection';

type AlgorithmChoice = PdfEncryptionAlgorithm | 'arcfour';

const ALGORITHMS: {
  id: AlgorithmChoice;
  label: string;
  disabled?: boolean;
  title?: string;
}[] = [
  { id: 'aes-128', label: '128-bit AES' },
  { id: 'aes-256', label: '256-bit AES' },
  {
    id: 'arcfour',
    label: '128-bit ARC-FOUR',
    disabled: true,
    title: 'Legacy — not supported',
  },
];

export default function PasswordProtectModal() {
  const open = usePdfStore((s) => s.passwordProtectModalOpen);
  const setOpen = usePdfStore((s) => s.setPasswordProtectModalOpen);
  const protection = usePdfStore((s) => s.protection);
  const enableProtection = usePdfStore((s) => s.enableProtection);
  const clearProtection = usePdfStore((s) => s.clearProtection);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [algorithm, setAlgorithm] = useState<PdfEncryptionAlgorithm>('aes-128');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setConfirm('');
    setShowPassword(false);
    setShowConfirm(false);
    setAlgorithm(protection?.algorithm ?? 'aes-128');
    setError(null);
  }, [open, protection?.algorithm]);

  if (!open) return null;

  const isClearing = !password && !confirm && !!protection?.enabled;
  const validationError =
    !password && !confirm
      ? null
      : validateProtectionPasswords(password, confirm);
  const canAccept =
    isClearing || (!!password && !!confirm && !validationError);

  const close = () => {
    setOpen(false);
    setError(null);
  };

  const handleAccept = () => {
    // Empty password clears protection when already enabled
    if (!password && !confirm) {
      if (protection?.enabled) {
        clearProtection();
        close();
        return;
      }
      setError('Enter a password');
      return;
    }

    const nextError = validateProtectionPasswords(password, confirm);
    if (nextError) {
      setError(nextError);
      return;
    }

    setProtectionPassword(password);
    enableProtection(algorithm);
    close();
  };

  return (
    <div className="pdfe-modal-overlay" role="presentation" onClick={close}>
      <div
        className="pdfe-modal pdfe-protect-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdfe-protect-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pdfe-modal-head">
          <h2 id="pdfe-protect-title">Password protect</h2>
          <button
            type="button"
            className="pdfe-modal-close"
            onClick={close}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="pdfe-protect-body">
          <section className="pdfe-protect-card">
            <h3 className="pdfe-protect-card-title">Document Opening Settings</h3>
            <div className="pdfe-protect-fields">
              <label className="pdfe-protect-field">
                <span>Password</span>
                <div className="pdfe-protect-input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    autoComplete="new-password"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="pdfe-protect-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
              </label>

              <label className="pdfe-protect-field">
                <span>Confirm password</span>
                <div className="pdfe-protect-input-wrap">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => {
                      setConfirm(e.target.value);
                      setError(null);
                    }}
                    autoComplete="new-password"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="pdfe-protect-eye"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    title={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
              </label>
            </div>
            {protection?.enabled && (
              <p className="pdfe-protect-hint">
                Leave both fields empty and Accept to remove protection.
              </p>
            )}
          </section>

          <section className="pdfe-protect-card">
            <h3 className="pdfe-protect-card-title">Encryption settings</h3>
            <p className="pdfe-protect-algo-label">Encryption algorithm:</p>
            <div className="pdfe-protect-radios" role="radiogroup" aria-label="Encryption algorithm">
              {ALGORITHMS.map((opt) => (
                <label
                  key={opt.id}
                  className={`pdfe-protect-radio${opt.disabled ? ' is-disabled' : ''}`}
                  title={opt.title}
                >
                  <input
                    type="radio"
                    name="pdfe-encryption-algo"
                    value={opt.id}
                    checked={
                      opt.id === 'arcfour' ? false : algorithm === opt.id
                    }
                    disabled={opt.disabled}
                    onChange={() => {
                      if (opt.id === 'arcfour') return;
                      setAlgorithm(opt.id);
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </section>

          {(error || (password && confirm && validationError)) && (
            <p className="pdfe-sig-modal-error">
              {error || validationError}
            </p>
          )}
        </div>

        <footer className="pdfe-modal-foot pdfe-protect-foot">
          <button type="button" className="btn btn-secondary" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAccept}
            disabled={!canAccept}
          >
            Accept
          </button>
        </footer>
      </div>
    </div>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"
        />
        <path
          fill="currentColor"
          d="M3.3 2.1 2 3.4l18.6 18.6 1.3-1.3L3.3 2.1z"
        />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"
      />
    </svg>
  );
}
